import { SSO_PROVIDERS, isSsoProviderKey, type SsoProviderKey } from '@/lib/auth/ssoProviders'

/** Holds the key of the provider whose sign-in was rejected because no account is linked to it. */
export const PENDING_SSO_LINK_COOKIE_NAME = 'pending_sso_link'

export const PENDING_SSO_LINK_MAX_AGE_SECONDS = 1800

/**
 * Text Cognito echoes back in `error_description` when its PreSignUp Lambda rejects an
 * unlinked social identity. The string is thrown by Nile-Core's
 * `infra/cdk/lib/lambda_src/pre_sign_up/index.js`; the two must change together.
 */
export const UNLINKED_SSO_IDENTITY_ERROR_DESCRIPTION = 'Signups via external identity providers are not allowed'

export function isUnlinkedSsoIdentityError(errorDescription: string | null | undefined): boolean {
    return Boolean(errorDescription?.includes(UNLINKED_SSO_IDENTITY_ERROR_DESCRIPTION))
}

export function parsePendingSsoLinkProvider(cookieValue: string | null | undefined): SsoProviderKey | null {
    return isSsoProviderKey(cookieValue) ? SSO_PROVIDERS[cookieValue].key : null
}

/** The cookie holds only a provider key (no PII) and is non-httpOnly so the prompt can clear it client-side. */
export function getPendingSsoLinkCookieOptions() {
    const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN
    return {
        httpOnly: false,
        secure: process.env.NODE_ENV !== 'development',
        ...(cookieDomain ? { domain: cookieDomain } : {}),
        path: '/',
        sameSite: 'lax' as const,
        maxAge: PENDING_SSO_LINK_MAX_AGE_SECONDS,
    }
}

const SIGN_IN_METHODS_PATH = '/account/profile/sign-in-methods'

/**
 * The prompt appears on the account dashboard surfaces only, so it never interrupts a booking,
 * checkout or signup flow. The sign-in methods page already offers the same toggle.
 */
export function isSsoLinkPromptSurface(pathname: string): boolean {
    const normalized = pathname.replace(/\/$/, '') || '/'
    if (normalized === SIGN_IN_METHODS_PATH) return false
    return normalized === '/account' || normalized.startsWith('/account/')
}
